import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store. The `value`
// attribute seeds the calendar and names what a form reset restores.
export const props = Object.freeze({
  /**
   * name attribute used for the element's form value — required to participate in a native form submit.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Render the calendar inline instead of attaching to a hidden alt input. Required for the picker to be visible on its own — without it the calendar lives behind a hidden input and never paints, so a parent that shows and hides its own wrapper drives visibility instead.
   *
   * @type {Boolean|String}
   * @default false
   */
  inline: booleanAttribute(),
  /**
   * Selection mode. `"single"` is the default. `"range"` requires two clicks (start, then end); the change event only fires once both endpoints are picked. `"multiple"` lets the user toggle several individual dates.
   *
   * @type {String}
   * @default "single"
   * @values single, range, multiple
   * @storybook-control select
   */
  mode: {
    type: String,
    default: "single",
    validator: (value) => ["single", "range", "multiple"].includes(value),
  },
  /**
   * Convenience flag equivalent to `mode="range"`. Takes precedence over an explicit `mode` when truthy and `multiple` is not set.
   *
   * @type {Boolean|String}
   * @default false
   */
  range: booleanAttribute(),
  /**
   * Convenience flag equivalent to `mode="multiple"`. Takes precedence over both `range` and `mode` when truthy.
   *
   * @type {Boolean|String}
   * @default false
   */
  multiple: booleanAttribute(),
  /**
   * Format of the text the user reads, exposed as the `displayText` property. Uses flatpickr formatting tokens — `F j, Y` renders as `March 15, 2026`. Left unset it is `F j, Y`, extended to `F j, Y h:i K` when `enableTime` is set, so the chosen time is legible. The `change` and `update-model-value` payloads carry `valueFormat` instead.
   *
   * @type {String}
   * @default "F j, Y"
   */
  displayFormat: {
    type: String,
    default: undefined,
  },
  /**
   * Format of the value the element reports and submits, in flatpickr tokens. Independent of `displayFormat`, which is what the user reads. Left unset, flatpickr uses `Y-m-d`, extended to `Y-m-d H:i` when `enableTime` is set.
   *
   * @type {String}
   * @default undefined
   */
  valueFormat: {
    type: String,
    default: undefined,
  },
  /**
   * Earliest selectable date. Days before it are disabled. Accepts anything flatpickr parses — an ISO string, a `Date`, or a timestamp.
   *
   * @type {String}
   * @default undefined
   */
  minDate: {
    type: [String, Number, Date],
    default: undefined,
  },
  /**
   * Latest selectable date. Days after it are disabled. Accepts anything flatpickr parses — an ISO string, a `Date`, or a timestamp.
   *
   * @type {String}
   * @default undefined
   */
  maxDate: {
    type: [String, Number, Date],
    default: undefined,
  },
  /**
   * Month the calendar opens on, when that is not the month of the selection — a booking calendar opening on the month the user is browsing, say. Sets the rendered month only; it selects nothing.
   *
   * @type {String}
   * @default undefined
   */
  openToDate: {
    type: [String, Number, Date],
    default: undefined,
  },
  /**
   * Availability state per day, as a map of `Y-m-d` date to a state name — `{ "2026-06-10": "high" }`. Each state renders as `data-day-state` on the day cell and is styled by the `--sp-comp-date-picker-day-state-*` tokens. A day the map does not name carries no state. Bind it as an object (`:day-states="map"`) or assign the `dayStates` property; the `.prop` modifier does not work, because it sets a literal `day-states` property rather than camelizing. A plain `day-states` attribute carries the same map as JSON, for a caller with no JS.
   *
   * @type {Object}
   * @default undefined
   */
  dayStates: {
    type: [Object, String],
    default: undefined,
  },
  /**
   * Move focus into the calendar once it renders, so arrow keys drive it without a click first. Off by default, because a calendar inside a menu must not take focus from the field that opened it.
   *
   * @type {Boolean|String}
   * @default false
   */
  focusOnReady: booleanAttribute(),
  /**
   * Add a time to the selection. The calendar grows a time footer, and both the value and `displayFormat` carry the time.
   *
   * @type {Boolean|String}
   * @default false
   */
  enableTime: booleanAttribute(),
  /**
   * Disable the calendar — dates cannot be selected and the picker renders in a disabled state.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require a date before the form can be submitted. Presence-based, the way the platform reads a
   * boolean attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required field is submitted without a date. Names what this
   * particular field is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on flatpickr's visible (alt) input. Pass a
   * catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a shared `data-testid` attribute on every rendered calendar day cell.
   * Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  dayTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the previous-month control.
   * Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  prevMonthTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the next-month control.
   * Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  nextMonthTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Accessible name of the calendar when no visible label names the element. Set it as
   * `aria-label` on the element; a host `aria-label` alone names nothing, because the day
   * grid lives in the shadow root.
   *
   * @type {String}
   * @default undefined
   */
  ariaLabel: {
    type: String,
    default: undefined,
  },
  /**
   * Space-separated ids of the elements that label the calendar, resolved in the tree the
   * element itself sits in. Set it as `aria-labelledby` on the element. Where the browser
   * lacks ARIA element reflection the referenced text is captured at render time, so a
   * label whose text changes afterwards needs `aria-label`.
   *
   * @type {String}
   * @default undefined
   */
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
});
